import { SessionToken, authSessionUrlForLoginUrl } from './session-token'

export interface TimeOnTaskInstance {
  trackSession(taskName: string): Promise<void> | void
}

type TimeOnTaskConstructor = new (
  studentId: string,
  options: {
    product: string
    syncOptions: {
      getAuthCallback: () => Promise<string>
      syncUrl: string
    }
  },
) => TimeOnTaskInstance

type TrackPathnameOptions = {
  loginUrl: string
  path: string | null | undefined
  studentId: number | string
  timeOnTaskUrl: string
  TimeOnTaskClass?: TimeOnTaskConstructor
}

type CreateTrackerOptions = {
  authSessionUrl: string
  studentId: number | string
  timeOnTaskUrl: string
  TimeOnTaskClass?: TimeOnTaskConstructor
}

type TrackerState = {
  authSessionUrl: string
  studentId: string
  syncUrl: string
  timeOnTask: TimeOnTaskInstance
}

let trackerState: TrackerState | undefined = undefined

export function taskNameForPath(path: string | null | undefined) {
  if (!path) return null

  const trimmedPath = path.trim()
  if (trimmedPath.length === 0) return null

  try {
    return new URL(trimmedPath, 'http://localhost').pathname
  } catch {
    return null
  }
}

export function syncUrlForTimeOnTask(timeOnTaskUrl: string) {
  if (!timeOnTaskUrl) return '/api/v2/complete_events'

  const baseUrl = timeOnTaskUrl.endsWith('/') ? timeOnTaskUrl : `${timeOnTaskUrl}/`
  return new URL('api/v2/complete_events', baseUrl).href
}

async function resolveTimeOnTaskClass(TimeOnTaskClass?: TimeOnTaskConstructor) {
  if (TimeOnTaskClass) return TimeOnTaskClass

  const { TimeOnTask } = await import('@blakeelearning/totter')
  return TimeOnTask as TimeOnTaskConstructor
}

export async function getOrCreateTimeOnTaskTracker({
  authSessionUrl,
  studentId,
  timeOnTaskUrl,
  TimeOnTaskClass,
}: CreateTrackerOptions) {
  const normalisedStudentId = String(studentId)
  const syncUrl = syncUrlForTimeOnTask(timeOnTaskUrl)

  if (
    trackerState?.studentId === normalisedStudentId &&
    trackerState.authSessionUrl === authSessionUrl &&
    trackerState.syncUrl === syncUrl
  ) {
    return trackerState.timeOnTask
  }

  const ResolvedTimeOnTaskClass = await resolveTimeOnTaskClass(TimeOnTaskClass)
  const sessionToken = new SessionToken(authSessionUrl)

  const timeOnTask = new ResolvedTimeOnTaskClass(normalisedStudentId, {
    product: 'mathseeds_prime',
    syncOptions: {
      syncUrl,
      getAuthCallback: async () =>
        (await sessionToken.getTokenForStudent(normalisedStudentId)) ?? '',
    },
  })

  trackerState = {
    authSessionUrl,
    studentId: normalisedStudentId,
    syncUrl,
    timeOnTask,
  }

  return timeOnTask
}

export async function trackTimeOnTaskPath({
  loginUrl,
  path,
  studentId,
  timeOnTaskUrl,
  TimeOnTaskClass,
}: TrackPathnameOptions) {
  const taskName = taskNameForPath(path)
  if (!taskName) return

  const trackerOptions: CreateTrackerOptions = {
    authSessionUrl: authSessionUrlForLoginUrl(loginUrl),
    studentId,
    timeOnTaskUrl,
  }

  if (TimeOnTaskClass) {
    trackerOptions.TimeOnTaskClass = TimeOnTaskClass
  }

  const timeOnTask = await getOrCreateTimeOnTaskTracker(trackerOptions)

  await timeOnTask.trackSession(taskName)
}

export function resetTimeOnTaskTrackerForTests() {
  trackerState = undefined
}
